import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connectStore } from 'redux-box';
import { module as pictionary } from '@st/pictionary';
import { module as ui } from '@st/ui';
import { ThemeButton } from '@sc/custom.styled';
import { uploadFile } from 'services/api/file';
import Select, { components } from 'react-select';
import wordsInfoApi from 'services/api/wordsinfo';
import gradeApi from 'services/api/grade';
import useDebouncing from '@u/useDebouncing';

const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    position: "absolute",
  }),
  control: (provide) => ({
    ...provide,
    width: "100%",
    margin: "0 auto",
  }),
};

const CustomMenuList = (props) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.isLoadingMore && (
        <div style={{ textAlign: "center", padding: "8px", color: "#999", fontSize: 12 }}>
          Loading more…
        </div>
      )}
    </components.MenuList>
  );
};

function PictionaryFormModal({ pictionary, ui }) {
  const editData = window.pictionaryEditData || null;
  const [selectedWord, setSelectedWord] = useState(null);
  const [url, setUrl] = useState('');
  const [fileLoading, setFileLoading] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  const [itemOptions, setItemOptions] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemOffset, setItemOffset] = useState(0);
  const [itemHasNext, setItemHasNext] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemLoadingMore, setItemLoadingMore] = useState(false);

  const debouncedItemSearch = useDebouncing(itemSearch, 400);

  useEffect(() => {
    gradeApi.getAllGrades().then((res) => {
      const gOptions = (res || []).map(g => ({ label: g.name, value: g.id }));
      setGradeOptions(gOptions);
    }).catch(err => console.error(err));
  }, []);

  const fetchWords = useCallback(
    async ({ search = "", offset = 0, append = false }) => {
      append ? setItemLoadingMore(true) : setItemLoading(true);
      try {
        const data = await wordsInfoApi.getWordsInfo(offset, search, '');
        const newOptions = (data.result || []).map(w => ({ label: w.word, value: w.id }));
        setItemOptions(prev => (append ? [...prev, ...newOptions] : newOptions));
        setItemOffset(offset);
        setItemHasNext(!!data.has_next);
      } catch (err) {
        console.error(err);
      } finally {
        append ? setItemLoadingMore(false) : setItemLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchWords({ search: debouncedItemSearch, offset: 0, append: false });
  }, [debouncedItemSearch, fetchWords]);

  const loadMoreItems = useCallback(() => {
    if (itemHasNext && !itemLoadingMore && !itemLoading) {
      const nextOffset = itemOffset + 1;
      fetchWords({ search: debouncedItemSearch, offset: nextOffset, append: true });
    }
  }, [itemHasNext, itemLoadingMore, itemLoading, itemOffset, debouncedItemSearch, fetchWords]);

  const handleItemSearchInput = (inputValue, { action }) => {
    setItemSearch(inputValue);
  };

  useEffect(() => {
    if (editData) {
      if (editData.word_id && editData.word_id.word) {
        setSelectedWord({ label: editData.word_id.word, value: editData.word_id.id || editData.word_id.word });
      }
      setUrl(editData.image_url || editData.url || '');

      if (editData.grade && Array.isArray(editData.grade)) {
        const selectedG = editData.grade.map(g => {
          const matched = gradeOptions.find(opt => opt.value === g);
          return matched ? matched : { label: "Unknown Grade", value: g };
        });
        setSelectedGrade(selectedG);
      } else {
        setSelectedGrade([]);
      }
    } else {
      setSelectedWord(null);
      setUrl('');
      setSelectedGrade([]);
    }
  }, [editData, gradeOptions]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileLoading(true);
      uploadFile(file)
        .then((res) => {
          if (res && res.file) {
            setUrl(res.file);
          } else if (res) {
            setUrl(res);
          }
        })
        .catch((err) => {
          console.error("Upload failed:", err);
          alert("Image upload failed. Please try again.");
        })
        .finally(() => {
          setFileLoading(false);
        });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedWord) {
      alert("Please select a word.");
      return;
    }
    if (!selectedGrade || selectedGrade.length === 0) {
      alert("Please select at least one grade.");
      return;
    }
    const payload = {
      word_id: selectedWord.value,
      image_url: url,
      grade: selectedGrade.map(g => g.value)
    };
    if (editData) {
      pictionary.updateImage(editData.id, payload);
    } else {
      pictionary.createImage(payload);
    }
  };

  return (
    <div style={{ padding: '20px', minWidth: '450px', backgroundColor: '#fff', borderRadius: '8px' }}>
      <h2>{editData ? "Edit Image" : "Add Image"}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Associated Word</label>
          <Select
            className="dropdown-container"
            value={selectedWord}
            onChange={setSelectedWord}
            onInputChange={handleItemSearchInput}
            isLoading={itemLoading}
            options={itemOptions}
            filterOption={() => true}
            components={{ MenuList: CustomMenuList }}
            onMenuScrollToBottom={loadMoreItems}
            isLoadingMore={itemLoadingMore}
            placeholder={itemLoading ? "Loading..." : "Search for a word..."}
            styles={customStyles}
            menuPlacement="auto"
            menuPosition="fixed"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Grade</label>
          <Select
            className="dropdown-container"
            isMulti
            options={gradeOptions}
            value={selectedGrade}
            onChange={setSelectedGrade}
            placeholder="Select Grade(s)..."
            styles={customStyles}
            menuPlacement="auto"
            menuPosition="fixed"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '5px' }}>Image URL</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              required
              value={url}
              onChange={e => setUrl(e.target.value)}
              style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
            <div className="sf-file-upload-wrapper" style={{ marginTop: 0 }}>
              <label className="sf-custom-file-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={fileLoading}
                />
                {fileLoading ? "Uploading..." : "Upload File"}
              </label>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
          <ThemeButton type="button" onClick={() => ui.hideModal()}>Cancel</ThemeButton>
          <ThemeButton primary type="submit" disabled={fileLoading}>Save</ThemeButton>
        </div>
      </form>
    </div>
  );
}

export default connectStore({ pictionary, ui })(PictionaryFormModal);
